import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  LEAD_QUALIFICATION_FIELDS,
  VOICE_AUTO_FIELDS,
  isFreeVoiceField,
  leadPushBlockers,
  tidyFreeAnswer,
  suggestDealName,
  understandLocally,
  voiceQuestion,
  voiceSay,
  yesNo,
  type VoiceRecord,
  type VoiceValue,
} from '@wr/shared';
import { ApiError, post } from '../../api.js';
import { Icon } from '../../components/Icon.js';
import { Dialog } from '../../components/ui.js';
import { LISTS, contactPushBlockers } from '../../lists.js';
import { useOffline } from '../../offline/context.js';
import { newId } from '../../offline/ids.js';
import type { LocalRecord } from '../../offline/store.js';
import { useSignedIn } from '../../session.js';
import { stageFor, useStages } from '../leads/stages.js';
import { fullName } from '../people/data.js';
import { createSpeech, type Speech } from './speech.js';

export interface ReviewItem {
  kind: 'contact' | 'lead';
  id: string;
}

interface Line {
  who: 'ai' | 'me';
  text: string;
}

interface Step {
  field: string;
  /** A scan field to check: what the scan read. */
  verify?: string;
  /** An answer understood with a doubt, or a free answer as written, waiting for yes or no. */
  confirm?: VoiceValue;
}

/** "Skip", "pass", "next question": the person moves on by voice. */
const SKIP = /^(skip|pass|next question)\b/i;

const str = (v: unknown) => (typeof v === 'string' ? v : '');
const today = () => new Date().toISOString().slice(0, 10);

/** What is still to ask about a record (the fields the portal fills itself aside). */
function missingOf(
  kind: ReviewItem['kind'],
  record: LocalRecord,
  qualify: boolean,
  skipped: readonly string[],
): Step[] {
  if (kind === 'contact') {
    return contactPushBlockers(record)
      .filter((b) => !skipped.includes(b.field))
      .map((b) =>
        b.code === 'unverified'
          ? { field: b.field, verify: str(record[b.field]) }
          : { field: b.field },
      );
  }
  const steps: Step[] = leadPushBlockers(record)
    .map((b) => b.field as string)
    .filter((f) => !(VOICE_AUTO_FIELDS as readonly string[]).includes(f))
    .filter((f) => qualify || !(LEAD_QUALIFICATION_FIELDS as readonly string[]).includes(f))
    .map((field) => ({ field }));
  // The Project Call never blocks; asked once a lead is qualified (brief section 2).
  if (qualify && record.qualification === 'qualified' && !str(record.project_call_date)) {
    steps.push({ field: 'project_call_date' });
  }
  return steps.filter((s) => !skipped.includes(s.field));
}

/**
 * The voice review of To do (brief sections 6.9 and 8; prototype: the
 * review call): it goes through the records to document and asks only what
 * is missing, one question at a time; each answer is understood (by Claude,
 * or on the device without network), said back ("Got it: Italy") and saved
 * at once. Doubtful answers are confirmed first; scan fields are checked
 * ("I read ..., is that right?"). Skip and End at any time; typing works
 * everywhere, and is the only way where the browser cannot listen.
 */
export function ReviewCall({
  queue,
  onEnd,
  speech: given,
}: {
  queue: ReviewItem[];
  onEnd(filled: number): void;
  speech?: Speech;
}) {
  const { user, can } = useSignedIn();
  const { store, save } = useOffline();
  const stages = useStages();
  const speech = useMemo(() => given ?? createSpeech(), [given]);
  const qualify = can('leads.qualify');
  const [log, setLog] = useState<Line[]>([]);
  const [index, setIndex] = useState(0);
  const [step, setStep] = useState<Step | null>(null);
  const [record, setRecord] = useState<{ data: LocalRecord; about: VoiceRecord } | null>(null);
  const [voiceOn, setVoiceOn] = useState(speech.canListen);
  const [listening, setListening] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const filled = useRef(0);
  const skipped = useRef(new Map<string, string[]>());
  const introduced = useRef<string | null>(null);
  // Answers not understood, per question: "Sorry" is never said twice.
  const misses = useRef(new Map<string, number>());
  const typeBox = useRef<HTMLInputElement>(null);
  const handsFree = useRef(false);
  const logBox = useRef<HTMLDivElement>(null);
  const voiceRef = useRef(voiceOn);
  voiceRef.current = voiceOn;

  const say = useCallback(
    async (text: string) => {
      setLog((l) => [...l, { who: 'ai', text }]);
      if (voiceRef.current) await speech.speak(text);
    },
    [speech],
  );

  useEffect(() => {
    logBox.current?.scrollTo?.({ top: logBox.current.scrollHeight });
  }, [log]);

  /** The record as the device keeps it now, with what the review needs to ask. */
  const load = useCallback(
    async (item: ReviewItem) => {
      if (!store) return null;
      const data = await store.getRecord(item.kind, item.id);
      if (!data || data.deleted) return null;
      if (item.kind === 'contact') {
        return {
          data,
          about: {
            kind: 'contact' as const,
            title: fullName(data) || str(data.company) || 'a contact',
            first: str(data.first_name),
            company: str(data.company),
          },
        };
      }
      const links = (await store.listRecords('lead_contact')).filter((l) => l.lead_id === item.id);
      const primary = links.sort((a, b) => Number(b.is_primary) - Number(a.is_primary))[0];
      const contact = primary ? await store.getRecord('contact', String(primary.contact_id)) : null;
      return {
        data,
        about: {
          kind: 'lead' as const,
          title: str(data.company_name) || (contact ? fullName(contact) : '') || 'a lead',
          first: str(contact?.first_name),
          contactName: contact ? fullName(contact) : '',
          company: str(data.company_name),
          country: str(data.country),
        },
      };
    },
    [store],
  );

  /** What the portal fills itself on a lead: owner, deal name, stage, deal market. */
  const autofill = useCallback(
    async (item: ReviewItem, data: LocalRecord) => {
      if (item.kind !== 'lead' || !store) return data;
      const fields: Record<string, unknown> = {};
      if (!str(data.owner_user_id)) fields.owner_user_id = user.id;
      if (!str(data.deal_name) && str(data.company_name)) {
        const event = data.event_id ? await store.getRecord('event', String(data.event_id)) : null;
        fields.deal_name = suggestDealName(str(data.company_name), str(event?.event_code) || null);
      }
      if (!str(data.stage_id) && str(data.qualification)) {
        const stage = stageFor(data.qualification, stages);
        if (stage) fields.stage_id = stage;
      }
      if (!str(data.deal_market) && Array.isArray(data.applications) && data.applications[0]) {
        fields.deal_market = data.applications[0];
      }
      if (Object.keys(fields).length === 0) return data;
      await save({
        opId: newId(),
        entity: 'lead',
        id: item.id,
        action: 'upsert',
        changedAt: new Date().toISOString(),
        fields,
      });
      return { ...data, ...fields };
    },
    [store, save, stages, user.id],
  );

  /** The next question: of this record, else the next record; the end after the last. */
  const next = useCallback(
    async (from: number) => {
      for (let i = from; i < queue.length; i++) {
        const item = queue[i]!;
        const loaded = await load(item);
        if (!loaded) continue;
        const data = await autofill(item, loaded.data);
        const steps = missingOf(item.kind, data, qualify, skipped.current.get(item.id) ?? []);
        const first = steps[0];
        if (!first) {
          if (introduced.current === item.id) await say(`${loaded.about.title} is complete.`);
          continue;
        }
        setIndex(i);
        setRecord({ data, about: loaded.about });
        if (introduced.current !== item.id) {
          introduced.current = item.id;
          await say(
            item.kind === 'lead'
              ? `Next, ${loaded.about.title}${loaded.about.contactName ? `, with ${loaded.about.contactName}` : ''}.`
              : `Now the contact ${loaded.about.title}${loaded.about.company ? ` at ${loaded.about.company}` : ''}.`,
          );
        }
        setStep(first);
        await say(voiceQuestion(first.field, loaded.about, first.verify));
        return;
      }
      setStep(null);
      setRecord(null);
      setDone(true);
      await say('That is everything for now. Each answer is saved.');
    },
    [queue, load, autofill, qualify, say],
  );

  // The start: hello, how many, then the first question.
  const started = useRef(false);
  useEffect(() => {
    if (started.current || !store) return;
    started.current = true;
    const leads = queue.filter((q) => q.kind === 'lead').length;
    const contacts = queue.length - leads;
    const what = [
      leads ? `${leads} lead${leads > 1 ? 's' : ''}` : '',
      contacts ? `${contacts} contact${contacts > 1 ? 's' : ''}` : '',
    ]
      .filter(Boolean)
      .join(' and ');
    void (async () => {
      await say(
        `Hi ${user.firstName}. ${what || 'Nothing'} to complete. I only ask what is missing. ${
          speech.canListen ? 'Answer by voice, or type.' : 'Type your answers.'
        }`,
      );
      await next(0);
    })();
  }, [store, queue, user.firstName, say, next, speech.canListen]);

  // After a question, listen again when the person answers by voice.
  useEffect(() => {
    if (!step || !handsFree.current || !voiceOn || busy || listening) return;
    void listen();
    // listen is stable enough for this purpose: it reads the latest state through refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  async function saveAnswer(current: Step, value: VoiceValue) {
    const item = queue[index]!;
    const data = record!.data;
    const fields: Record<string, unknown> = { [current.field]: value };
    const flagged = Array.isArray(data.needs_verification)
      ? (data.needs_verification as string[])
      : [];
    if (item.kind === 'contact' && flagged.includes(current.field)) {
      fields.needs_verification = flagged.filter((f) => f !== current.field);
    }
    await save({
      opId: newId(),
      entity: item.kind,
      id: item.id,
      action: 'upsert',
      changedAt: new Date().toISOString(),
      fields,
    });
    filled.current += 1;
  }

  /** Claude's understanding, or the device's when Claude cannot answer. */
  async function understand(field: string, question: string, answer: string) {
    try {
      return await post<{ value: VoiceValue | null; sure: boolean }>('/voice/understand', {
        field,
        question,
        answer,
        today: today(),
      });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) throw error;
      const value = understandLocally(field, answer, LISTS, {
        companyCountry: record?.about.country,
        today: today(),
      });
      return { value, sure: true };
    }
  }

  /**
   * Not understood (or not confirmed): asked again once; the second time,
   * the person is offered to type the answer or to skip, and the call stops
   * listening by itself (round 2).
   */
  async function missed(field: string, again: string) {
    const key = `${queue[index]?.id}:${field}`;
    const count = (misses.current.get(key) ?? 0) + 1;
    misses.current.set(key, count);
    setStep({ field });
    if (count === 1) return await say(again);
    handsFree.current = false;
    await say('I still did not get it. Type the answer below, or skip this question.');
    typeBox.current?.focus();
  }

  async function answer(text: string) {
    const current = step;
    if (!current || !record || busy || !text.trim()) return;
    setBusy(true);
    setLog((l) => [...l, { who: 'me', text }]);
    try {
      if (SKIP.test(text.trim())) {
        skip(current.field);
        await say('Skipped, it stays to complete.');
        return await next(index);
      }
      const reply = yesNo(text);
      // A doubtful or free answer, confirmed or not.
      if (current.confirm !== undefined && reply !== null) {
        if (reply) {
          await saveAnswer(current, current.confirm);
          await say(
            isFreeVoiceField(current.field)
              ? 'Got it, saved.'
              : `Got it: ${voiceSay(current.field, current.confirm, LISTS)}.`,
          );
          return await next(index);
        }
        return await missed(
          current.field,
          isFreeVoiceField(current.field)
            ? 'What should I write, then?'
            : voiceQuestion(current.field, record.about),
        );
      }
      // A scan field to check.
      if (current.verify !== undefined && reply !== null) {
        if (reply) {
          await saveAnswer(current, current.verify);
          await say('Got it, checked.');
          return await next(index);
        }
        setStep({ field: current.field });
        return await say('What is it, then?');
      }
      if (current.field === 'project_call_date' && /not (yet|booked)|^no\b/i.test(text.trim())) {
        skip(current.field);
        await say('Noted: it becomes a HubSpot task.');
        return await next(index);
      }
      const question = voiceQuestion(current.field, record.about, current.verify);
      // "Same country": the company's, which only this device knows here.
      const same =
        current.field === 'installation_country' && record.about.country && /\bsame\b/i.test(text)
          ? record.about.country
          : null;
      const { value, sure } = same
        ? { value: same, sure: true }
        : await understand(current.field, question, text);
      if (value === null) {
        return await missed(current.field, 'Sorry, I did not get that. Can you say it again?');
      }
      // A free answer is always read back as written, then confirmed.
      if (isFreeVoiceField(current.field)) {
        const written = tidyFreeAnswer(String(value));
        setStep({ field: current.field, confirm: written });
        return await say(`I wrote: ${written} Is that right?`);
      }
      if (!sure) {
        setStep({ field: current.field, confirm: value });
        return await say(`Did you say ${voiceSay(current.field, value, LISTS)}? Yes or no?`);
      }
      await saveAnswer(current, value);
      await say(`Got it: ${voiceSay(current.field, value, LISTS)}.`);
      await next(index);
    } catch {
      await say('This device could not save it. Nothing was lost: try again.');
    } finally {
      setBusy(false);
    }
  }

  function skip(field: string) {
    const item = queue[index];
    if (!item) return;
    skipped.current.set(item.id, [...(skipped.current.get(item.id) ?? []), field]);
  }

  async function onSkip() {
    if (!step || busy) return;
    speech.silence();
    setLog((l) => [...l, { who: 'me', text: 'Skip' }]);
    skip(step.field);
    setStep(null);
    await say('Skipped, it stays to complete.');
    await next(index);
  }

  async function listen() {
    if (!speech.canListen || listening || !step) return;
    speech.silence();
    handsFree.current = true;
    setListening(true);
    try {
      const words = await speech.listen();
      setListening(false);
      if (words) await answer(words);
    } catch {
      setListening(false);
      handsFree.current = false;
    }
  }

  function end() {
    speech.silence();
    speech.stop();
    onEnd(filled.current);
  }

  const progress = record
    ? `${index + 1} of ${queue.length}: ${record.about.title}`
    : done
      ? 'Done'
      : 'Starting';

  return (
    <Dialog
      title="Review call"
      className="wide"
      onClose={end}
      footer={
        <>
          <button
            type="button"
            className="btn left"
            disabled={!step || busy}
            onClick={() => void onSkip()}
          >
            Skip this question
          </button>
          <button type="button" className="btn" onClick={end}>
            End the call
          </button>
        </>
      }
    >
      <div className="rc-top">
        <span className="rc-live" />
        <span id="rc-prog">{progress}</span>
        <button
          type="button"
          className="btn sm"
          aria-pressed={voiceOn}
          onClick={() => {
            if (voiceOn) speech.silence();
            setVoiceOn(!voiceOn);
          }}
        >
          {voiceOn ? 'AI voice on' : 'AI voice off'}
        </button>
      </div>
      <div className="rc-log" ref={logBox} aria-live="polite" aria-label="Conversation">
        {log.map((line, i) => (
          <div key={i} className={`rc-b ${line.who}`}>
            {line.text}
          </div>
        ))}
      </div>
      <div className="rc-ans">
        <button
          type="button"
          className={`mic rc-mic${listening ? ' rec' : ''}`}
          aria-label={listening ? 'Listening' : 'Answer by voice'}
          disabled={!speech.canListen || !step || busy}
          onClick={() => (listening ? speech.stop() : void listen())}
        >
          <Icon name="mic" />
        </button>
        {!speech.canListen && (
          <p className="note rc-note">This browser cannot listen: type the answers.</p>
        )}
        <form
          className="rc-type"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            const value = typed.trim();
            if (!value) return;
            handsFree.current = false;
            setTyped('');
            void answer(value);
          }}
        >
          <input
            ref={typeBox}
            type="text"
            aria-label="Your answer"
            placeholder="Or type the answer"
            value={typed}
            disabled={!step}
            onChange={(e) => setTyped(e.target.value)}
          />
          <button type="submit" className="btn sm" disabled={!step || busy}>
            Send
          </button>
        </form>
      </div>
    </Dialog>
  );
}
